import { toast } from 'sonner';
import type { Services } from '@/infrastructure/services';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { PublishIncompleteError, submitProjectForReview as submitProjectStories } from '@/components/publish-flow/reviewSubmissions';
import type { ProjectPublishSubmitArgs } from '@/components/publish-flow/types';
import { ensureProjectMapImage } from '@/utils/ensureProjectMapImage';
import { invalidateShareLinks } from '@/hooks/useShareLink';

type ProjectStatus = 'draft' | 'live' | 'archived' | 'pending';

export async function readProjectStatus(projectId: string): Promise<ProjectStatus | null> {
  const { data, error } = await listAll(page => v1.GET('/api/v1/scenes', { params: { query: { projectUniqueId: [projectId], ...page } } }));
  if (error) {
    console.error('Error checking scene instances:', error);
    return null;
  }
  const statuses = new Set(data.map(scene => scene.status));
  if (statuses.has('live')) return 'live';
  if (statuses.has('pending')) return 'pending';
  if (statuses.has('archived') && !statuses.has('draft') && !statuses.has('template')) return 'archived';
  return 'draft';
}

interface SubmitProjectParams {
  projectId: string;
  userId: string | undefined;
  args: ProjectPublishSubmitArgs;
}

function storyNamesOf(args: ProjectPublishSubmitArgs): Record<string, string> {
  return Object.fromEntries(args.scenes.map(scene => [scene.sceneOriginId, scene.storyName || scene.sceneOriginName]));
}

// The modal's footer has one line, so the gaps are listed story by story in a toast
function incompleteMessage(error: PublishIncompleteError, args: ProjectPublishSubmitArgs): string {
  const names = storyNamesOf(args);
  const lines = error.errors.map(e => `${e.storyUniqueId ? `"${names[e.storyUniqueId] ?? 'A story'}"` : 'Project'}: ${e.message}`);
  toast.error(`Nothing was submitted yet. ${lines.join(' ')}`, { id: 'project-publish-incomplete', duration: 15000 });
  return `Nothing was submitted. ${lines.length === 1 ? lines[0] : `${lines.length} things need fixing first.`}`;
}

// All the stories or none, in one request
export async function submitProjectForReview(services: Services, { projectId, userId, args }: SubmitProjectParams): Promise<ProjectStatus | null> {
  try {
    await submitProjectStories(services, {
      projectId,
      devices: args.devices,
      visibility: args.visibility,
      listing: {
        creatorName: args.creatorName,
        projectName: args.projectName,
        projectDescription: args.projectDescription,
        projectTags: args.projectTags,
      },
      stories: args.scenes.map(scene => ({
        storyId: scene.sceneOriginId,
        sceneId: scene.sceneInstanceId,
        name: scene.storyName,
        introduction: scene.sceneIntroduction,
      })),
    });
  } catch (error) {
    if (error instanceof PublishIncompleteError) throw new Error(incompleteMessage(error, args), { cause: error });
    throw error;
  }
  for (const scene of args.scenes) invalidateShareLinks(scene.sceneOriginId);

  if (userId) {
    try {
      await ensureProjectMapImage(services, projectId, userId);
    } catch (mapError) {
      console.error('Update project map image after submit failed', mapError);
    }
  }
  return readProjectStatus(projectId);
}
